import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';
import { getImageUrl } from '../config/constants';
import { useCart } from '../context';

interface ProductQuickViewProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProductQuickView: React.FC<ProductQuickViewProps> = ({ product, isOpen, onClose }) => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [quantity, setQuantity] = useState(1);

  if (!isOpen || !product) return null;

  // Get all images from the product
  const productImages = (() => {
    const images = product.images;
    if (images && Array.isArray(images) && images.length > 0) {
      return images
        .filter((img: any) => img.type === 'image')
        .map((img: any) => getImageUrl(img.url));
    }
    return product.image_url ? [getImageUrl(product.image_url)] : [];
  })();

  const currentImage = productImages[currentImageIndex] || '';

  // Get collection name or category
  const category = product.collection_name || product.collection?.name || 'Productos';

  // Calculate effective price (with discount if available)
  const effectivePrice = product.discount_price || product.price;
  const hasDiscount = product.discount_price && product.discount_price < product.price;

  // Get variants
  const variants = product.variants || [];
  const hasVariants = variants.length > 0;

  // Get available sizes
  const availableSizes: string[] = hasVariants
    ? [...new Set(variants.map((v: any) => v.talla).filter(Boolean))] as string[]
    : product.talla
    ? product.talla.split(',').map((s: string) => s.trim())
    : [];

  // Calculate stock
  const totalStock = hasVariants
    ? variants.reduce((sum: number, v: any) => sum + (v.stock_quantity || 0), 0)
    : product.stock_quantity;

  const isOutOfStock = totalStock === 0;
  const isLowStock = totalStock > 0 && totalStock <= 5;

  const handleAddToCart = () => {
    const itemToAdd = {
      ...product,
      selectedVariant,
      selectedQuantity: quantity,
    };
    addItem(itemToAdd);
    onClose();
  };

  const handleViewFullDetails = () => {
    navigate(`/products/${product.id}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div className="relative bg-canvas-primary rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 bg-canvas-secondary hover:bg-canvas-tertiary rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl"
          aria-label="Close"
        >
          <svg className="w-6 h-6 text-fg-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6 md:p-10">
          {/* Image Section */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-xl overflow-hidden bg-canvas-secondary">
              {currentImage ? (
                <img
                  src={currentImage}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <svg className="w-24 h-24 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}

              {/* Badges */}
              <div className="absolute top-4 right-4 flex flex-col gap-2">
                {hasDiscount && (
                  <div className="bg-green-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    -{Math.round(product.discount_percent ?? 0)}%
                  </div>
                )}
                {isOutOfStock && (
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                    Agotado
                  </div>
                )}
                {isLowStock && !isOutOfStock && (
                  <div className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    ¡Stock bajo!
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnail Images */}
            {productImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {productImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      currentImageIndex === index
                        ? 'border-fg-highlight-primary shadow-md'
                        : 'border-stroke-primary hover:border-fg-highlight-primary/50'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.name} - ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="flex flex-col">
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 text-[10px] text-fg-highlight-primary uppercase tracking-[0.15em] font-bold bg-gradient-to-r from-canvas-secondary to-canvas-secondary/50 px-3 py-1.5 rounded-md border border-fg-highlight-primary/20">
                {category}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-fg-primary mb-4">
              {product.name}
            </h2>

            <div className="flex items-baseline gap-3 mb-6">
              {hasDiscount && (
                <p className="text-xl font-semibold text-fg-tertiary line-through">
                  {new Intl.NumberFormat('es-CO', {
                    style: 'currency',
                    currency: 'COP',
                    maximumFractionDigits: 0,
                  }).format(product.price)}
                </p>
              )}
              <p className={`text-3xl font-bold ${hasDiscount ? 'text-green-600' : 'text-fg-highlight-primary'}`}>
                {new Intl.NumberFormat('es-CO', {
                  style: 'currency',
                  currency: 'COP',
                  maximumFractionDigits: 0,
                }).format(effectivePrice)}
              </p>
            </div>

            {product.description && (
              <p className="text-fg-secondary mb-6 leading-relaxed">
                {product.description}
              </p>
            )}

            {/* Size Selection */}
            {availableSizes.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-fg-primary mb-3">
                  Talla:
                </label>
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((size: string) => {
                    const variant = hasVariants
                      ? variants.find((v: any) => v.talla === size)
                      : null;
                    const isAvailable = variant ? variant.stock_quantity > 0 : true;

                    return (
                      <button
                        key={size}
                        onClick={() => setSelectedVariant(variant)}
                        disabled={!isAvailable}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          selectedVariant?.talla === size || (!hasVariants && selectedVariant === size)
                            ? 'bg-fg-highlight-primary text-white shadow-md'
                            : isAvailable
                            ? 'bg-canvas-secondary text-fg-primary hover:bg-canvas-tertiary border border-stroke-primary'
                            : 'bg-canvas-tertiary text-fg-tertiary cursor-not-allowed opacity-50'
                        }`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            {!isOutOfStock && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-fg-primary mb-3">
                  Cantidad:
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 bg-canvas-secondary hover:bg-canvas-tertiary rounded-lg flex items-center justify-center transition-colors duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-xl font-bold text-fg-primary w-12 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(totalStock, quantity + 1))}
                    className="w-10 h-10 bg-canvas-secondary hover:bg-canvas-tertiary rounded-lg flex items-center justify-center transition-colors duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="mb-6">
              {isOutOfStock ? (
                <div className="flex items-center gap-2 text-red-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">Sin stock</span>
                </div>
              ) : isLowStock ? (
                <div className="flex items-center gap-2 text-orange-500">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">¡Solo quedan {totalStock} unidades!</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-green-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">En stock</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-auto">
              <button
                onClick={handleAddToCart}
                disabled={isOutOfStock}
                className={`flex-1 px-6 py-4 rounded-xl font-bold uppercase tracking-wide transition-all duration-300 flex items-center justify-center gap-2 ${
                  isOutOfStock
                    ? 'bg-canvas-tertiary text-fg-tertiary cursor-not-allowed'
                    : 'bg-fg-highlight-primary text-white hover:bg-opacity-90 shadow-lg hover:shadow-xl'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                Agregar al Carrito
              </button>
              <button
                onClick={handleViewFullDetails}
                className="flex-1 px-6 py-4 bg-canvas-secondary hover:bg-canvas-tertiary text-fg-primary rounded-xl font-bold uppercase tracking-wide transition-all duration-300 border-2 border-stroke-primary hover:border-fg-highlight-primary"
              >
                Ver Detalles Completos
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductQuickView;
